import {ActionTree, GetterTree, Module, MutationTree} from 'vuex'
import {AuthState, RootState} from '@/types/store'
import {$} from 'simpli-web-sdk'
import {Role} from '@/enums/Role'
import AuthRequest from '@/model/request/AuthRequest'
import AuthResponse from '@/model/response/AuthResponse'
import ResetPasswordRequest from '@/model/request/ResetPasswordRequest'
import RecoverPasswordRequest from '@/model/request/RecoverPasswordRequest'
import Responsavel from '@/model/resource/Responsavel'
import * as Helper from '@/helpers'
import ChangeTemporaryPasswordRequest from '@/model/request/ChangeTemporaryPasswordRequest'
import {sha256} from 'js-sha256'
import ResponsavelUsuario from '@/model/resource/ResponsavelUsuario'

// initial state
const state: AuthState = {
  token: null,
  role: null,
  queryToken: null,
  adminToken: null,
  responsavel: null,
  members: [],
  cachePath: null,
  regraExibicaoGrupoEconomico: null,
  usuario: null,
  eventListener: {
    signIn: [],
    auth: [],
    signOut: [],
  },
}

// getters
const getters: GetterTree<AuthState, RootState> = {
  isLogged: ({token, queryToken}) => Boolean(token || queryToken),
  token: ({token}) => token,
  role: ({role}) => role,
  queryToken: ({queryToken}) => queryToken,
  adminToken: ({adminToken}) => adminToken,
  responsavel: ({responsavel}) => responsavel || new Responsavel(),
  members: ({members}) => members,
  firstMemberOrNull: ({members}) => members.length === 1 ? members[0] : null,
  cachePath: ({cachePath}) => cachePath,
  regraExibicaoGrupoEconomico: ({regraExibicaoGrupoEconomico}) => regraExibicaoGrupoEconomico,
  usuario: ({usuario}) => usuario || new ResponsavelUsuario(),
}

// actions
const actions: ActionTree<AuthState, RootState> = {
  /**
   * Sign in account
   * @param state
   * @param commit
   * @param getters
   * @param request
   * @param welcome
   */
  signIn: async ({state, commit, getters, dispatch}, request: AuthRequest) => {
    const authResponse = new AuthResponse()

    await authResponse.signIn(request)

    if (authResponse.firstAccess) {
      $.router.push({
        name: 'changeTemporaryPassword',
        params: { user: request.cnpj || '', oldPassword: request.senha || '' },
      })
      return
    } else if (authResponse.firstAccess === false) {
      const authRequest = new AuthRequest()
      authRequest.cnpj = request.cnpj
      authRequest.senha = request.senha ? sha256(request.senha) : ''
      await dispatch('completeEmailSignIn', authRequest)
      return
    }

    if (authResponse.token) localStorage.setItem('token', authResponse.token)

    commit('POPULATE_TOKEN')

    let path = '/cockpit'
    if (authResponse.role === Role.SAC) {
      path = '/reports/delivery/sac'
    } else if (authResponse.role === Role.RASTREAMENTO) {
      path = '/tracking'
    }

    if (authResponse.responsavel) {
      $.snotify.info(authResponse.responsavel.$tag, $.t('system.info.welcome'))
    }

    Helper.push(getters.cachePath || path)

    commit('SET_CACHE_PATH', null)

    state.eventListener.signIn.forEach((item) => item(authResponse))
  },

  /**
   * Verifies authorization and refresh user info.
   * Note: If it is not logged then dispatches signOut
   * @param dispatch
   * @param commit
   * @param getters
   * @param spinner
   */
  auth: async ({dispatch, commit, getters}, spinner = 'auth') => {
    if (getters.queryToken) return

    if (getters.adminToken) {
      const authResp = new AuthResponse()

      try {
        await authResp.getTokenForAdmin(getters.adminToken)
      } catch {
        dispatch('signOut')
        return
      } finally {
        commit('SET_ADMIN_TOKEN', null)
      }

      if (authResp.token) localStorage.setItem('token', authResp.token)

      commit('POPULATE', authResp)

      Helper.push('/cockpit')
    }

    commit('POPULATE_TOKEN')

    if (!getters.isLogged) {
      commit('SET_CACHE_PATH', $.route.path)

      Helper.error('system.error.unauthorized')
      dispatch('signOut')
      return
    }

    const authResponse = new AuthResponse()

    await authResponse.authenticate(spinner)

    commit('POPULATE', authResponse)

    Helper.setIdFilial(getters.firstMemberOrNull && getters.firstMemberOrNull.$id || 0)
    Helper.setAllowNotification(getters.responsavel && getters.responsavel.receberNotificacoesCarga || false)
    Helper.setRole(authResponse.role && authResponse.role.toString() || '')
    Helper.setUsuarioNome(authResponse.usuario && authResponse.usuario.nome || '')
    Helper.setAdminNome(authResponse.administrador && authResponse.administrador.nome || '')

    state.eventListener.auth.forEach((item) => item(authResponse))
  },

  /**
   * Sign out account
   * @param state
   * @param commit
   * @param showError
   */
  signOut: ({state, commit}) => {
    Helper.push('/sign-in')

    commit('FORGET')
    state.eventListener.signOut.forEach((item) => item())
    localStorage.clear()
  },

  /**
   * Change logged user responsavel
   * @param state
   * @param commit
   * @param showError
   */
  changeResponsavel: ({state, commit}) => {
    Helper.push('/sign-in/filial')

    const userData = localStorage.getItem('ud')

    commit('FORGET')
    state.eventListener.signOut.forEach((item) => item())
    localStorage.clear()

    if (userData) localStorage.setItem('ud', userData)
  },

  /**
   * Reset password
   * @param context
   * @param request
   */
  resetPassword: async (context, request: ResetPasswordRequest) => {
    await new AuthResponse().resetPassword(request)
    Helper.successAndPush('system.success.resetPassword', '/sign-in')
  },

  /**
   * Recover password
   * @param context
   * @param request
   */
  recoverPassword: async (context, request: RecoverPasswordRequest) => {
    await new AuthResponse().recoverPassword(request)
    Helper.successAndPush('system.success.recoverPassword', '/sign-in')
  },

  /**
   * Change password
   * @param context
   * @param request
   */
  changePassword: async ({dispatch, getters, commit}, request: Responsavel) => {
    const token = await new AuthResponse().changePassword(request)
    if (token) localStorage.setItem('token', token)
    commit('POPULATE_TOKEN')

    Helper.success('system.success.recoverPassword')
  },

  /**
   * Complete sign-in by email: resolves branch (filial) access after password
   * validation and either goes to branch selection or logs in directly.
   * @param context
   * @param request
   */
  completeEmailSignIn: async ({state, commit, getters, dispatch}, request: AuthRequest) => {
    const authResponse = new AuthResponse()

    try {
      $.await.init('completeEmailSignIn')

      await authResponse.validateCode(request)

      const {data} = await AuthResponse.listResponsavelFilial(request)

      if (data.length > 1) {
        const userData = {u: request.cnpj, up: request.senha}
        localStorage.setItem('ud', JSON.stringify(userData))

        $.router.push({
          name: 'signInFilial',
          params: { user: request.cnpj || '', password: request.senha || '' },
        })
      } else {
        request.idResponsavel = data[0].idResponsavelPk
        await dispatch('validateFilial', request)
        $.await.done('completeEmailSignIn')
      }
    } catch {
      //
    }
  },

  validateFilial: async ({state, commit, getters}, request: AuthRequest) => {
    const authResponse = new AuthResponse()

    try {
      await authResponse.validateFilial(request)

      if (authResponse.token) {
        localStorage.setItem('token', authResponse.token)
      }

      commit('POPULATE_TOKEN')

      let path = '/cockpit'
      if (authResponse.role === Role.SAC) {
        path = '/reports/delivery/sac'
      } else if (authResponse.role === Role.RASTREAMENTO) {
        path = '/tracking'
      }

      Helper.push(getters.cachePath || path)

      commit('SET_CACHE_PATH', null)

      state.eventListener.signIn.forEach((item) => item(authResponse))
    } catch {
      //
    }
  },

  /**
   * Send code to email
   * @param context
   * @param request
   */
  sendCode: async (context, request: AuthRequest) => {
    await new AuthResponse().sendCode(request)
    Helper.success('system.success.codeSent')
  },

  /**
   * Change temporary password
   * @param context
   * @param request
   */
  changeTemporaryPassword: async ({dispatch, getters, commit}, request: ChangeTemporaryPasswordRequest) => {
    await new AuthResponse().changeTemporaryPassword(request)
    Helper.success('system.success.changeTemporaryPassword')

    const authRequest = new AuthRequest()
    authRequest.cnpj = request.user
    authRequest.senha = sha256(request.newPassword || '')
    await dispatch('completeEmailSignIn', authRequest)
  },

  /**
   * On SignIn Event
   * @param dispatch
   * @param callback
   */
  onSignIn: ({dispatch}, callback) => dispatch('addEventListener', {name: 'signIn', callback}),

  /**
   * On Auth Event
   * @param dispatch
   * @param callback
   */
  onAuth: ({dispatch}, callback) => dispatch('addEventListener', {name: 'auth', callback}),

  /**
   * On SignOut Event
   * @param dispatch
   * @param callback
   */
  onSignOut: ({dispatch}, callback) => dispatch('addEventListener', {name: 'signOut', callback}),

  /**
   * Add event listener
   * @param commit
   * @param payload {name, callback}
   */
  addEventListener: ({commit}, payload) => commit('ADD_EVENT_LISTENER', payload),

  /**
   * Remove event listener
   * @param commit
   * @param payload
   */
  removeEventListener: ({commit}, payload) => commit('REMOVE_EVENT_LISTENER', payload),
}

// mutations
const mutations: MutationTree<AuthState> = {
  // Populate token mutation
  POPULATE_TOKEN(state) {
    state.token = localStorage.getItem('token') || null
  },

  // Populate query token mutation
  SET_QUERY_TOKEN(state, val) {
    state.queryToken = val || null
  },

  SET_ADMIN_TOKEN(state, val) {
    state.adminToken = val || null
  },

  // Populate user and plan mutation
  POPULATE(state, response: AuthResponse) {
    state.role = response.role
    state.responsavel = response.responsavel
    state.members = response.members
    state.regraExibicaoGrupoEconomico = response.regraExibicaoGrupoEconomico
    state.usuario = response.usuario
  },

  // Forget mutation
  FORGET(state) {
    state.token = null
    state.role = null
    state.responsavel = null
    state.members = []
    state.regraExibicaoGrupoEconomico = null
    state.usuario = null

    localStorage.removeItem('token')
    localStorage.removeItem('ud')
  },

  // Set Cache Path mutation
  SET_CACHE_PATH(state, val) {
    const blacklist = [
      /\/sign-in/,
      /\/password\/reset/,
      /\/password\/recover\/\S+/,
      /\/delivery\/get\/\d+\/\S+/,
    ]

    for (const pattern of blacklist) {
      if (pattern.exec(val)) {
        state.cachePath = null
        return
      }
    }

    state.cachePath = val
  },

  // Add Event Listener mutation
  ADD_EVENT_LISTENER(state, {name, callback}) {
    state.eventListener[name].push(callback)
  },

  // Remove Event Listener mutation
  REMOVE_EVENT_LISTENER(state, {name, callback}) {
    if (callback) {
      const index = state.eventListener[name].findIndex((item) => item === callback)
      state.eventListener[name].splice(index, 1)
    } else {
      state.eventListener[name] = []
    }
  },
}

const namespaced: boolean = true
export const auth: Module<AuthState, RootState> = {
  namespaced,
  state,
  getters,
  actions,
  mutations,
}
